import isString from 'lodash/isString';
import {
  cloneElement,
  isValidElement,
  useEffect,
  useRef,
  useState,
} from 'react';

import type {
  Id,
  DialogContent,
  IDialogOptions,
  IConfirmationDialogProps,
  IAlertDialogProps,
  IDialog,
} from '@/infra/dialog/DialogEventManager';
import {
  dialogEventManager,
  DialogEvent,
  DialogTypes,
} from '@/infra/dialog/DialogEventManager';

const useDialogContainer = () => {
  const [dialogIds, setDialogIds] = useState<Id[]>([]);

  const dialogsOpened = useRef(new Map<Id, IDialog>()).current;
  const isDialogActived = (id: Id) => dialogIds.indexOf(id) !== -1;

  useEffect(() => {
    dialogEventManager
      .on(DialogEvent.Show, buildDialog)
      .on(DialogEvent.Close, dialogId => removeDialogById(dialogId));
  }, []);

  function removeDialogById(dialogId: Id) {
    const hasConfirmationToDelete = !!dialogsOpened.get(dialogId);

    if (!hasConfirmationToDelete) {
      return;
    }

    dialogsOpened.delete(dialogId);
    setDialogIds(state => state.filter(id => id !== dialogId));
  }

  function buildDialog(
    type: DialogTypes,
    content: DialogContent,
    { ...options }: IDialogOptions,
  ) {
    switch (type) {
      case DialogTypes.Confirmation:
        return buildConfirmation(content, options as IConfirmationDialogProps);
      case DialogTypes.Alert:
        return buildAlert(content, options as IAlertDialogProps);
      default:
        break;
    }
  }

  function buildConfirmation(
    content: DialogContent,
    { ...options }: IConfirmationDialogProps,
  ) {
    const { dialogId } = options;
    const closeConfirmation = (isAccepted: boolean) => {
      dialogEventManager.emit(DialogEvent.Close, dialogId, isAccepted);
    };

    const confirmationProps: IConfirmationDialogProps = {
      ...options,
      onConfirm: () => {
        closeConfirmation(true);
        if (options.onConfirm) {
          options.onConfirm();
        }
      },
      onRefuse: () => {
        closeConfirmation(false);
        if (options.onRefuse) {
          options.onRefuse();
        }
      },
    };

    let confirmationContent = content;

    if (isValidElement(content) && !isString(content.type)) {
      confirmationContent = cloneElement(content, {
        // TODO Fix this
        // @ts-ignore
        confirmationProps,
      });
    }

    appendDialog(
      confirmationContent,
      confirmationProps,
      DialogTypes.Confirmation,
    );
  }

  function buildAlert(
    content: DialogContent,
    { ...options }: IAlertDialogProps,
  ) {
    const { dialogId } = options;
    const closeDialog = (isAccepted: boolean) => {
      dialogEventManager.emit(DialogEvent.Close, dialogId, isAccepted);
    };

    const alertProps: IAlertDialogProps = {
      ...options,
      onConfirm: () => {
        closeDialog(true);
        if (options.onConfirm) {
          options.onConfirm();
        }
      },
    };

    let alertContent = content;

    if (isValidElement(content) && !isString(content.type)) {
      alertContent = cloneElement(content, {
        // TODO Fix this
        // @ts-ignore
        alertProps,
      });
    }

    appendDialog(alertContent, alertProps, DialogTypes.Alert);
  }

  function appendDialog(
    content: DialogContent,
    dialogProps: IDialogOptions,
    type: DialogTypes,
  ) {
    const { dialogId } = dialogProps;

    const dialog = {
      content,
      props: dialogProps,
      type,
    };

    dialogsOpened.set(dialogId, dialog);

    setDialogIds(state => [...state, dialogId]);
  }

  function getDialogsOpened() {
    const toRender = Array.from(dialogsOpened.values());

    return toRender;
  }

  return {
    getDialogsOpened,
    isDialogActived,
  };
};

export default useDialogContainer;
